import { useState, MouseEvent } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import {
  GridRowSelectionModel,
  GridApiPremium,
} from "@mui/x-data-grid-premium";
import _ from "lodash";
import dvdlocator from "../../../apis/dvdlocator";
import { ModalSwitchState } from "./PricetoolModalSwitch";
import { BulkStringOperationConfig } from "../../../components/datagrid/types";
import { removeHtmlFromString } from "../../../utilities/removeHtmlFromString";
import { BigNumber } from "bignumber.js";

interface ListingTableButtonsPanelProps {
  rowSelectionModel: GridRowSelectionModel;
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error";
  }) => void;
  apiRef: React.MutableRefObject<GridApiPremium>;
  getSelectedRows?: () => any[];
  onBulkUpdate?: (
    ids: (string | number)[],
    field: string,
    operations: BulkStringOperationConfig[]
  ) => Promise<{ success: boolean; error?: string }>;
  setModalSwitchState?: (state: ModalSwitchState) => void;
  appendOutputLog?: (content: any) => void;
}

export function ListingTableButtonsPanel({
  rowSelectionModel,
  setSnackbar,
  apiRef,
  getSelectedRows,
  onBulkUpdate,
  appendOutputLog,
  setModalSwitchState,
}: ListingTableButtonsPanelProps) {
  // State for dropdown menus
  const [analysisAnchorEl, setAnalysisAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [warehouseAnchorEl, setWarehouseAnchorEl] =
    useState<null | HTMLElement>(null);
  const [curatorAnchorEl, setCuratorAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [openAnchorEl, setOpenAnchorEl] = useState<null | HTMLElement>(null);
  const [appendAnchorEl, setAppendAnchorEl] = useState<null | HTMLElement>(
    null
  );

  // State for Amazon calculator selectors
  const [amzCalcFillMode, setAmzCalcFillMode] = useState("Cogs-Only");

  // Helper to check if rows are selected
  const hasSelection = rowSelectionModel.ids.size > 0;
  const hasSingleSelection = rowSelectionModel.ids.size === 1;

  // Menu handlers
  const handleAnalysisClick = (event: MouseEvent<HTMLElement>) => {
    setAnalysisAnchorEl(event.currentTarget);
  };
  const handleAnalysisClose = () => {
    setAnalysisAnchorEl(null);
  };

  const handleWarehouseClick = (event: MouseEvent<HTMLElement>) => {
    setWarehouseAnchorEl(event.currentTarget);
  };
  const handleWarehouseClose = () => {
    setWarehouseAnchorEl(null);
  };

  const handleCuratorClick = (event: MouseEvent<HTMLElement>) => {
    setCuratorAnchorEl(event.currentTarget);
  };
  const handleCuratorClose = () => {
    setCuratorAnchorEl(null);
  };

  const handleOpenClick = (event: MouseEvent<HTMLElement>) => {
    setOpenAnchorEl(event.currentTarget);
  };
  const handleOpenClose = () => {
    setOpenAnchorEl(null);
  };

  const handleAppendClick = (event: MouseEvent<HTMLElement>) => {
    setAppendAnchorEl(event.currentTarget);
  };
  const handleAppendClose = () => {
    setAppendAnchorEl(null);
  };

  // Button action handlers
  const handleAmzPriceCalc = async () => {
    const fillMode = amzCalcFillMode;

    const productData = getSelectedRows ? getSelectedRows()[0] : null;

    if (!productData.product.product_asin)
      return alert("Can't calculate Amazon fees for product with no ASIN.");

    // Ensuring we have a Cost of Good value
    let cogs = productData.product.cogs;

    const providedCogs = window.prompt(
      `Requesting user input ${!cogs ? "[Required]" : ""}
                                              \nTITLE: ${removeHtmlFromString(
                                                productData["product"][
                                                  "product_title"
                                                ]
                                              )} 
                                              \nASIN: ${
                                                productData.product.product_asin
                                              }.
                                              \n\nPlease input a value for: 'product.cogs'`,
      cogs ? new BigNumber(cogs).dividedBy(100).toFixed(2) : ""
    );

    if (!providedCogs) return;
    cogs = new BigNumber(providedCogs).multipliedBy(100).toFixed(0);

    // Get Amazon fee estimates
    let estimateResponse;
    try {
      estimateResponse = await dvdlocator.post("/amazon-fees", {
        asins: [productData.product.product_asin],
        productIds: [productData.product.product_id],
      });
    } catch (err) {
      console.log(err);
      alert(`Failed to get amazon fees. Please try again.`);
      return;
    }

    // Get pricing data and update columns
    const fees = estimateResponse.data.fees[0];
    try {
      const response = await dvdlocator.post("/amazon-price-calculator", {
        requests: [
          {
            variableFee: new BigNumber(fees.variableFee).toFixed(0),
            fbaFee: new BigNumber(fees.fbaFee).toFixed(0),
            productId: productData.product.product_id,
            type: fillMode,
            price: productData.product_listings.price,
            cogs,
          },
        ],
      });

      if (!fees.fbaFee)
        alert("WARNING: Returned FBA Fee was $0.00\n\nPotential Amazon Error");

      apiRef.current.dataSource.fetchRows();

      const logObject = {
        ...response.data[0],
        weight: productData.product.packaged_weight_ounces,
        componentQty: productData.product.component_count,
        mediaTypes: productData.media_types.map((m: any) => m.label).join(","),
        title: productData.product.product_title,
        asin: productData.product.product_asin,
        log_type: "ap",
      };

      if (appendOutputLog) appendOutputLog(logObject);
    } catch (err) {
      console.log(err);
      alert(
        "Amazon pricing update failed. This probably wasn't your fault. Try again or contact dev team."
      );
      return;
    }
  };

  const handleMSKUs = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];
    const mskus: string[] = [];
    selectedRows.forEach((row: any) => {
      mskus.push(row["product"]["msku"]);
    });
    if (mskus.length) {
      if (setModalSwitchState) {
        setModalSwitchState({
          modalName: "MessageModal",
          selectedRows: [],
          appendStatus: false,
          appendErrors: [],
          columnNames: [],
          mode: "product",
          message: mskus.join(","),
          messageType: "msku",
          messageCopy: true,
        });
      }
    } else {
      alert("Please select row(s)");
    }
    handleAnalysisClose();
  };

  const handleSumColumn = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "SumColumn",
        selectedRows: selectedRows,
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "product",
      });
    }
    handleAnalysisClose();
  };

  const handleNSQtoASQ = async () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one row",
        severity: "error",
      });
      handleAnalysisClose();
      return;
    }

    try {
      for (const row of selectedRows) {
        const updatedRow = _.cloneDeep(row);
        const nextShipQty = row.product?.next_amz_ship_qty;

        updatedRow["product.amz_ship_qty"] = nextShipQty;

        await apiRef.current.dataSource.editRow({
          rowId: row.id,
          updatedRow: updatedRow,
          previousRow: row,
        });
      }

      setSnackbar({
        open: true,
        message: "Successfully pushed Next Ship Qty to Amz Ship Qty!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error pushing Next Ship Qty to Amz Ship Qty:", error);
      setSnackbar({
        open: true,
        message: "Error pushing Next Ship Qty to Amz Ship Qty.",
        severity: "error",
      });
    }

    handleAnalysisClose();
  };

  const handleYSRtoNSQ = async () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one row",
        severity: "error",
      });
      handleAnalysisClose();
      return;
    }

    try {
      for (const row of selectedRows) {
        const updatedRow = _.cloneDeep(row);
        const yomShipRec = row.product?.yom_ship_rec;

        updatedRow["product.next_amz_ship_qty"] = yomShipRec;

        await apiRef.current.dataSource.editRow({
          rowId: row.id,
          updatedRow: updatedRow,
          previousRow: row,
        });
      }

      setSnackbar({
        open: true,
        message: "Successfully pushed Yom Ship Rec to Next Ship Qty!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error pushing Yom Ship Rec to Next Ship Qty:", error);
      setSnackbar({
        open: true,
        message: "Error pushing Yom Ship Rec to Next Ship Qty.",
        severity: "error",
      });
    }

    handleAnalysisClose();
  };

  const handleAutoMarginScript = async () => {
    try {
      await dvdlocator.post("/calc-auto-margin");
      setSnackbar({
        open: true,
        message: "Successfully started Auto Margin task!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error starting Auto Margin script:", error);
      setSnackbar({
        open: true,
        message: "Error starting Auto Margin script.",
        severity: "error",
      });
    }
    handleAnalysisClose();
  };

  const handleAmazonOrdersScript = async () => {
    try {
      await dvdlocator.post("/invoke-amazon-orders");
      setSnackbar({
        open: true,
        message: "Successfully started Amazon Orders task!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error starting Amazon Orders script:", error);
      setSnackbar({
        open: true,
        message: "Error starting Amazon Orders script.",
        severity: "error",
      });
    }
    handleAnalysisClose();
  };

  const handleATSScript = async () => {
    try {
      const res = await dvdlocator.post("/invoke-ats");
      if (res.data.includes("Task already running")) {
        setSnackbar({
          open: true,
          message: "ATS task already running.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Successfully started ATS task!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error starting ATS script:", error);
      setSnackbar({
        open: true,
        message: "Error starting ATS script.",
        severity: "error",
      });
    }
    handleAnalysisClose();
  };

  const handleMAVCGScript = async () => {
    try {
      const res = await dvdlocator.post("/invoke-mavcg");
      if (res.data.includes("Task already running")) {
        setSnackbar({
          open: true,
          message: "MAVCG task already running.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Successfully started MAVCG task!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error starting MAVCG script:", error);
      setSnackbar({
        open: true,
        message: "Error starting MAVCG script.",
        severity: "error",
      });
    }
    handleAnalysisClose();
  };

  const handleRetireUnretire = async () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one row",
        severity: "error",
      });
      handleAnalysisClose();
      return;
    }

    // Extract product IDs
    const productIds = selectedRows.map((row: any) => row.product?.product_id);

    // Determine if we're retiring or unretiring
    // If any product is already retired, we're unretiring
    let retiring = true;
    for (const row of selectedRows) {
      if (row.product?.retired) {
        retiring = false;
        break;
      }
    }

    try {
      await dvdlocator.post("/products/retire", {
        productIds,
        retired: retiring,
      });

      setSnackbar({
        open: true,
        message: `Successfully ${
          retiring ? "retired" : "unretired"
        } product(s)!`,
        severity: "success",
      });

      // Reload the data grid
      if (apiRef?.current) {
        apiRef.current.dataSource.fetchRows();
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: `There was an error ${
          retiring ? "retiring" : "unretiring"
        } the product(s)!`,
        severity: "error",
      });
    }

    handleAnalysisClose();
  };

  const handleRefreshAnalyticsColumns = async () => {
    try {
      await dvdlocator.post("/refresh-analytics-db-columns");
      setSnackbar({
        open: true,
        message: "Successfully started task! Please wait a moment for update.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error starting task:", error);
      setSnackbar({
        open: true,
        message: "Error starting task.",
        severity: "error",
      });
    }
    handleAnalysisClose();
  };

  const handleAmazonBarcode = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    if (selectedRows.length === 0) {
      alert("Please select at least one row");
      handleWarehouseClose();
      return;
    }

    const rowData = [];

    if (selectedRows.length === 1) {
      const count = prompt(
        "How many copies of this barcode would you like?",
        "1"
      );
      const numCopies = parseInt(count || "1", 10);

      for (let i = 0; i < numCopies; i++) {
        rowData.push(selectedRows[0]);
      }
    } else {
      // For multiple rows, add amz_ship_qty amount of each row
      for (let i = 0; i < selectedRows.length; i++) {
        const amz_ship_qty = selectedRows[i].product?.amz_ship_qty;

        if (amz_ship_qty && amz_ship_qty > 0) {
          for (let j = 0; j < amz_ship_qty; j++) {
            rowData.push(selectedRows[i]);
          }
        } else {
          // if amz_ship_qty empty or 0, just add one copy
          rowData.push(selectedRows[i]);
        }
      }
    }

    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "BarcodeModal",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "product",
        barCodeType: "amazon",
        rowData: rowData,
      });
    }
    handleWarehouseClose();
  };

  const handleGenerateSlotLabel = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    if (selectedRows.length === 0) {
      alert("Please select at least one row");
      handleWarehouseClose();
      return;
    }

    let rowData = [];

    if (selectedRows.length === 1) {
      const count = prompt(
        "How many copies of this label would you like?",
        "1"
      );
      const numCopies = parseInt(count || "1", 10);

      for (let i = 0; i < numCopies; i++) {
        rowData.push(selectedRows[0]);
      }
    } else {
      rowData = selectedRows;
    }

    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "SlotLabelModal",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [],
        mode: "product",
        rowData: rowData,
        slotLabelType: "product",
      });
    }
    handleWarehouseClose();
  };

  const handlePrependListory = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.listory", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleCuratorClose();
  };

  const handleClearSortColumn = async () => {
    if (
      !window.confirm(
        "Are you sure you would like to clear the data in the Sort column?"
      )
    ) {
      handleCuratorClose();
      return;
    }

    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    if (selectedRows.length === 0) {
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: "Please select at least one row",
          severity: "error",
        });
      }
      handleCuratorClose();
      return;
    }

    try {
      for (const row of selectedRows) {
        const updatedRow = _.cloneDeep(row);

        // Update using dot-notation format for proper change detection
        updatedRow["product.sort"] = "";

        // Update the row via the dataSource to send server update
        await apiRef.current.dataSource.editRow({
          rowId: row.id,
          updatedRow: updatedRow,
          previousRow: row,
        });
      }

      // Success notification handled by dataSource.editRow
    } catch (error) {
      console.error("Error clearing Sort column:", error);
      // Error notification already handled by dataSource.editRow
    }

    handleCuratorClose();
  };

  const handleProductFamilies = async () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    for (const row of selectedRows) {
      if (
        row.product_families_view?.families &&
        row.product_families_view.families.length > 0
      ) {
        const families = row.product_families_view.families
          .split(",")
          .map((fam: string) => fam.trim());

        for (const family of families) {
          await new Promise((r) => setTimeout(r, 500));
          if (family !== "") {
            const params = new URLSearchParams();
            params.append("tab", "all");
            params.set(
              "listings_filter_product_families_view.families_operator",
              "contains"
            );
            params.set(
              "listings_filter_product_families_view.families_value",
              family
            );

            params.set(
              "inventory_filter_supply_families_view.families_operator",
              "contains"
            );
            params.set(
              "inventory_filter_supply_families_view.families_value",
              family
            );

            const url = `/datatables/pricetool?${params.toString()}`;
            window.open(url, "_blank");
          }
        }
      }
    }
    handleOpenClose();
  };

  const handleOpenListing = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    const asinSet = new Set(
      selectedRows.map((row: any) => row.product?.product_asin).filter(Boolean)
    );

    asinSet.forEach((asin) => {
      const params = new URLSearchParams();
      params.set("tab", "all");
      params.set("listings_filter_product.product_asin_operator", "equals");
      params.set("listings_filter_product.product_asin_value", asin);
      params.set(
        "inventory_filter_inventory_associated_product_asins_view.product_asins_operator",
        "contains"
      );
      params.set(
        "inventory_filter_inventory_associated_product_asins_view.product_asins_value",
        asin
      );
      window.open(`/datatables/pricetool?${params.toString()}`, "_blank");
    });
    handleOpenClose();
  };

  const handleRepricer = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    selectedRows.forEach((row: any) => {
      const msku = row.product?.msku;
      if (msku) {
        window.open(
          `https://dashboard-5.repricer.com/repricer?fai_fca_id%5B%5D=52849&fsf_query=${msku}`,
          "_blank"
        );
      }
    });
    handleOpenClose();
  };

  const handleOrderHistory = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    selectedRows.forEach((row: any) => {
      const msku = row.product?.msku;
      if (msku) {
        window.open(
          `https://sellercentral.amazon.com/orders-v3/search?page=1&date-range=last-7&q=${msku}&qt=sku`,
          "_blank"
        );
      }
    });
    handleOpenClose();
  };

  const handleOrders = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    selectedRows.forEach((row: any) => {
      const msku = row.product?.msku;
      if (msku) {
        const params = new URLSearchParams();
        params.set("orders_filter_product.msku_operator", "equals");
        params.set("orders_filter_product.msku_value", msku);
        window.open(`/datatables/amazon-orders?${params.toString()}`, "_blank");
      }
    });
    handleOpenClose();
  };

  const handleBuyBox = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    selectedRows.forEach((row: any) => {
      const asin = row.product?.product_asin;
      if (asin) {
        window.open(`https://www.amazon.com/dp/${asin}`, "_blank");
      }
    });
    handleOpenClose();
  };

  const handleReStock = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    selectedRows.forEach((row: any) => {
      const fnsku = row.product?.fnsku;
      if (fnsku) {
        window.open(
          `https://sellercentral.amazon.com/inventory/view/PRICING?tbla_myitable=sort:%7B%22sortOrder%22%3A%22DESCENDING%22%7D;search:${fnsku};pagination:1;`,
          "_blank"
        );
      }
    });
    handleOpenClose();
  };

  const handleManageInventory = () => {
    const selectedRows = getSelectedRows ? getSelectedRows() : [];

    selectedRows.forEach((row: any) => {
      const asin = row.product?.product_asin;
      if (asin && asin.trim()) {
        const trimmedAsin = asin.trim();
        window.open(
          `https://sellercentral.amazon.com/myinventory/inventory?fulfilledBy=all&page=1&pageSize=10&searchField=all&searchTerm=${trimmedAsin}&sort=sales_desc&status=all`,
          "_blank"
        );
      }
    });
    handleOpenClose();
  };

  const handlePrependPartnum = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.part_number", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependAmazonQueue = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.amz_queue", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependOrders = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.orders", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependWn = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.wn", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependWWO = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.wwo", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependAN = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.an", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependPickStatus = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.pick_status", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependAmazonQueueAndNextShipQty = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [
          { name: "product.amz_queue", type: "string" },
          { name: "product.next_amz_ship_qty", type: "number" },
        ],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependPricingNotes = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.pricing_notes", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependThreadingNotes = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.threading_notes", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  const handlePrependCurationNotes = () => {
    if (setModalSwitchState) {
      setModalSwitchState({
        modalName: "PrependForm",
        selectedRows: [],
        appendStatus: false,
        appendErrors: [],
        columnNames: [{ name: "product.curation_notes", type: "string" }],
        mode: "product",
        getSelectedRows: getSelectedRows,
        onBulkUpdate: onBulkUpdate,
        setSnackbar: setSnackbar,
      });
    }
    handleAppendClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        padding: 2,
        borderBottom: "1px solid #e0e0e0",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {/* Amazon Calculator Mode Selectors */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
          value={amzCalcFillMode}
          onChange={(e) => setAmzCalcFillMode(e.target.value)}
          title="Select the input mode for the Amazon Price Calculator. Cogs-Only: Only asks for the Cost of the Good."
          sx={{ fontSize: "10px", height: "100%" }}
        >
          <MenuItem value="Cogs-Only">Cogs-Only</MenuItem>
        </Select>
      </FormControl>

      {/* Amz Price Calc Button */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleAmzPriceCalc}
        disabled={!hasSingleSelection}
        title="Amazon Price Calculator"
      >
        Amz Price Calc
      </Button>

      {/* Analysis Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleAnalysisClick}
        title="Analyze Item"
        endIcon={<ArrowDropDown />}
      >
        Analysis
      </Button>
      <Menu
        anchorEl={analysisAnchorEl}
        open={Boolean(analysisAnchorEl)}
        onClose={handleAnalysisClose}
      >
        <MenuItem onClick={handleMSKUs} title="Copy selected UPCs">
          MSKUs
        </MenuItem>
        <MenuItem
          onClick={handleSumColumn}
          title="Sum up the values of a specific column for selected rows."
        >
          Sum Column
        </MenuItem>
        <MenuItem
          onClick={handleNSQtoASQ}
          title="Push the current Next Ship Qty value into the Amz Ship Qty column."
        >
          NSQ to ASQ
        </MenuItem>
        <MenuItem
          onClick={handleYSRtoNSQ}
          title="Push the current Yom Ship Rec value into the Next Ship Qty column."
        >
          YSR to NSQ
        </MenuItem>
        <MenuItem onClick={handleAutoMarginScript}>Auto Margin Script</MenuItem>
        <MenuItem onClick={handleAmazonOrdersScript}>
          Amazon Orders Script
        </MenuItem>
        <MenuItem onClick={handleATSScript}>ATS Script</MenuItem>
        <MenuItem onClick={handleMAVCGScript}>MAVCG Script</MenuItem>
        <MenuItem onClick={handleRetireUnretire} disabled={!hasSelection}>
          Retire/Unretire
        </MenuItem>
        <MenuItem onClick={handleRefreshAnalyticsColumns}>
          Refresh Analytics Columns
        </MenuItem>
      </Menu>

      {/* Warehouse Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleWarehouseClick}
        title="Ware House Actions"
        endIcon={<ArrowDropDown />}
      >
        Warehouse
      </Button>
      <Menu
        anchorEl={warehouseAnchorEl}
        open={Boolean(warehouseAnchorEl)}
        onClose={handleWarehouseClose}
      >
        <MenuItem
          onClick={handleAmazonBarcode}
          disabled={!hasSelection}
          title="Amazon Barcode"
        >
          Amazon Barcode
        </MenuItem>
        <MenuItem
          onClick={handleGenerateSlotLabel}
          disabled={!hasSelection}
          title="Generate warehouse shelving slot label for selected row(s)"
        >
          Generate Slot Label
        </MenuItem>
      </Menu>

      {/* Curator Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleCuratorClick}
        title="Jons Precious Menu"
        endIcon={<ArrowDropDown />}
      >
        Curator
      </Button>
      <Menu
        anchorEl={curatorAnchorEl}
        open={Boolean(curatorAnchorEl)}
        onClose={handleCuratorClose}
      >
        <MenuItem
          onClick={handlePrependListory}
          title="Prepend / Remove from Listory"
        >
          Prepend Listory
        </MenuItem>
        <MenuItem onClick={handleClearSortColumn} title="Clear Sort Column ">
          Clear "Sort" Column
        </MenuItem>
      </Menu>

      {/* Open Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpenClick}
        title="Search for Items"
        endIcon={<ArrowDropDown />}
      >
        Open
      </Button>
      <Menu
        anchorEl={openAnchorEl}
        open={Boolean(openAnchorEl)}
        onClose={handleOpenClose}
      >
        <MenuItem onClick={handleProductFamilies} title="Open Families">
          Product Families
        </MenuItem>
        <MenuItem
          onClick={handleOpenListing}
          title="Open Listing and Inventory rows in Pricetool"
        >
          Open Listing
        </MenuItem>
        <MenuItem
          onClick={handleRepricer}
          title="Open ItemIDs in Repricer Express"
        >
          Repricer
        </MenuItem>
        <MenuItem
          onClick={handleOrderHistory}
          title="Open UPC in Amazon Order History"
        >
          Order History
        </MenuItem>
        <MenuItem
          onClick={handleOrders}
          title="Open UPC in Amazon Orders table"
        >
          Orders
        </MenuItem>
        <MenuItem onClick={handleBuyBox} title="Open ASIN in Buy Box">
          Buy Box
        </MenuItem>
        <MenuItem onClick={handleReStock} title="Open UPC in Re-Stock / Price">
          Re-Stock
        </MenuItem>
        <MenuItem
          onClick={handleManageInventory}
          title="Open inventory management for ASIN in Seller Central"
        >
          Manage Inventory
        </MenuItem>
      </Menu>

      {/* Prepend Dropdown */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleAppendClick}
        title="Prepend to Field"
        endIcon={<ArrowDropDown />}
      >
        Prepend
      </Button>
      <Menu
        anchorEl={appendAnchorEl}
        open={Boolean(appendAnchorEl)}
        onClose={handleAppendClose}
      >
        <MenuItem
          onClick={handlePrependPartnum}
          title="Prepend / Remove from Part Number"
        >
          Partnum
        </MenuItem>
        <MenuItem
          onClick={handlePrependAmazonQueue}
          title="Prepend / Remove from Amazon Queue"
        >
          Amazon Queue
        </MenuItem>
        <MenuItem
          onClick={handlePrependOrders}
          title="Prepend / Remove from Orders"
        >
          Orders
        </MenuItem>
        <MenuItem
          onClick={handlePrependWn}
          title="Prepend / Remove from Warehouse Notes"
        >
          Wn
        </MenuItem>
        <MenuItem
          onClick={handlePrependWWO}
          title="Prepend / Remove from Warehouse Work Orders"
        >
          WWO
        </MenuItem>
        <MenuItem
          onClick={handlePrependAN}
          title="Prepend / Remove from Analyst Notes"
        >
          AN
        </MenuItem>
        <MenuItem
          onClick={handlePrependPickStatus}
          title="Prepend / Remove from Pick Status"
        >
          Pick Status
        </MenuItem>
        <MenuItem
          onClick={handlePrependAmazonQueueAndNextShipQty}
          title="Prepend/remove from Amazon Queue and Next Ship Qty"
        >
          Amazon Queue & Next Ship Qty
        </MenuItem>
        <MenuItem
          onClick={handlePrependPricingNotes}
          title="Prepend / Remove from Pricing Notes"
        >
          Pricing Notes
        </MenuItem>
        <MenuItem
          onClick={handlePrependThreadingNotes}
          title="Prepend / Remove from Threading Notes"
        >
          Threading Notes
        </MenuItem>
        <MenuItem
          onClick={handlePrependCurationNotes}
          title="Prepend / Remove from Curation Notes"
        >
          Curation Notes
        </MenuItem>
      </Menu>
    </Box>
  );
}
